import React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export function SearchableCombobox({
  value = "",
  onValueChange,
  items = [],
  getItemId = (item) => item.id,
  getItemLabel = (item) => item.label,
  selectedId = "",
  onSelect,
  allowCustom = false,
  placeholder = "",
  disabled = false,
  emptyMessage = "Sin resultados",
  className,
  inputClassName,
  renderItemAction,
  isItemDisabled = () => false,
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef(null);
  const normalizedQuery = String(value || "").trim().toLowerCase();

  const filteredItems = React.useMemo(() => {
    if (!normalizedQuery) return items;
    return items.filter((item) => String(getItemLabel(item) || "").toLowerCase().includes(normalizedQuery));
  }, [items, normalizedQuery, getItemLabel]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    onSelect?.(item);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <Input
        value={value}
        onChange={(event) => {
          onValueChange?.(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && open) {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className={inputClassName}
      />
      {open && !disabled && (
        <div className="absolute z-[70] mt-1 max-h-56 w-full overflow-auto rounded-md border bg-popover py-1 text-sm shadow-md">
          {filteredItems.length === 0 ? (
            <button
              type="button"
              className="flex w-full px-3 py-2 text-left text-muted-foreground hover:bg-accent"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setOpen(false)}
            >
              {allowCustom && value.trim() ? `Usar «${value.trim()}»` : emptyMessage}
            </button>
          ) : (
            filteredItems.map((item) => {
              const id = getItemId(item);
              const label = getItemLabel(item);
              const isSelected = String(id) === String(selectedId);
              return (
                <div key={id} className="flex items-center">
                  <button
                  type="button"
                  disabled={isItemDisabled(item)}
                  className={cn("flex w-full px-3 py-2 text-left hover:bg-accent", isSelected && "bg-accent")}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(item)}
                >
                  {label}
                </button>
                  {renderItemAction?.(item)}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
